import type { BehaviorAnalysis, BehaviorSummary, InterviewConfig, Message, InterviewReport } from '../types';
import { authFetch } from './auth';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://mmbang.site';

// 面试配置类型（传递给chat接口）
export interface ChatInterviewConfig {
  level: string;
  jobType: string;
  type: string;
  interviewer?: {
    name?: string;
    personality?: string;
  };
  userInfo?: {
    nickname?: string;
    techStacks?: string[];
  };
  currentRound: number;
  mode?: 'interview' | 'report';
  isFirstMessage?: boolean;
  isPressureInterview?: boolean;
  hasJobDescription?: boolean;
  jobDescription?: string;
}

// 获取面试官性格描述
function getInterviewerPersonality(
  interviewer?: InterviewConfig['interviewer'],
): string {
  if (!interviewer) return '专业、友善';
  return interviewer.personality || '专业、友善';
}

// 获取面试官名字
function getInterviewerName(
  interviewer?: InterviewConfig['interviewer'],
): string {
  if (!interviewer) return '面试官';
  return interviewer.name || '面试官';
}

// 构建传递给chat接口的面试配置
function buildChatInterviewConfig(
  config: InterviewConfig,
  messages: Message[],
  mode: 'interview' | 'report' = 'interview',
): ChatInterviewConfig {
  // 计算当前轮次
  const interviewerMessageCount = messages.filter(
    (m) => m.role === 'interviewer',
  ).length;
  const currentRound = interviewerMessageCount + 1;

  // 判断是否是首轮消息
  const isFirstMessage = messages.length === 0;

  return {
    level: config.level,
    jobType: config.jobType,
    type: config.type,
    interviewer: config.interviewer
      ? {
          name: getInterviewerName(config.interviewer),
          personality: getInterviewerPersonality(config.interviewer),
        }
      : undefined,
    userInfo: config.userInfo,
    currentRound,
    mode,
    isFirstMessage,
    isPressureInterview: config.isPressureInterview,
    hasJobDescription: config.hasJobDescription,
    jobDescription: config.jobDescription,
  };
}

// 生成面试官的回应（开场白、问题、追问、结束语）
export async function generateInterviewerResponse(
  config: InterviewConfig,
  messages: Message[],
): Promise<string> {
  const isFirstMessage = messages.length === 0;

  // 构建消息历史（只传递对话历史，不添加额外提示）
  const apiMessages: {
    role: 'system' | 'user' | 'assistant';
    content: string;
  }[] = [];

  // 添加历史对话
  messages.forEach((m) => {
    apiMessages.push({
      role: m.role === 'interviewer' ? 'assistant' : 'user',
      content: m.content,
    });
  });

  // 构建面试配置
  const interviewConfig = buildChatInterviewConfig(
    config,
    messages,
    'interview',
  );

  const response = await authFetch(`${API_BASE_URL}/api/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages: apiMessages,
      temperature: 0.8,
      interviewConfig,
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `生成回应失败: ${response.status}`);
  }

  const data = await response.json();
  return data.content || getDefaultResponse(config, isFirstMessage);
}

// 检查是否应该结束面试
export function shouldEndInterview(messages: Message[]): boolean {
  // 检查面试官是否说了结束语
  const lastInterviewerMessage = messages
    .filter((m) => m.role === 'interviewer')
    .pop();

  if (lastInterviewerMessage) {
    const content = lastInterviewerMessage.content;
    // 只匹配系统提示词中要求的结束语
    return content.includes('面试到此结束，谢谢你的参与');
  }

  return false;
}

// 生成面试报告
export async function generateReport(
  config: InterviewConfig,
  messages: Message[],
  behaviorSummary?: BehaviorSummary | null,
): Promise<InterviewReport> {
  // 构建消息历史（只包含对话内容，不包含系统提示）
  const apiMessages: {
    role: 'system' | 'user' | 'assistant';
    content: string;
  }[] = [];

  // 添加完整对话历史
  messages.forEach((m) => {
    apiMessages.push({
      role: m.role === 'interviewer' ? 'assistant' : 'user',
      content: m.content,
    });
  });

  // 构建面试配置（报告模式）
  const interviewConfig = buildChatInterviewConfig(config, messages, 'report');

  try {
    const response = await authFetch(`${API_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: apiMessages,
        temperature: 0.5,
        interviewConfig,
        behaviorSummary: behaviorSummary
          ? {
              averageConfidence: behaviorSummary.averageConfidence,
              averageNervousness: behaviorSummary.averageNervousness,
              averageEngagement: behaviorSummary.averageEngagement,
              eyeContactRate: behaviorSummary.eyeContactRate,
              smileFrequency: behaviorSummary.smileFrequency,
              postureStats: behaviorSummary.postureStats,
              gazeStats: behaviorSummary.gazeStats,
            }
          : undefined,
      }),
    });

    if (!response.ok) {
      throw new Error('生成报告失败');
    }

    const data = await response.json();
    const content = data.content || '';

    // 尝试解析 JSON
    try {
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const report = JSON.parse(jsonMatch[0]);
        return {
          totalScore: report.totalScore,
          dimensions: report.dimensions || {
            expression: 80,
            technical: 80,
            logic: 80,
            communication: 80,
            professionalism: 80,
            learning: 80,
            overall: 80,
          },
          strengths: report.strengths || getDefaultReport().strengths,
          suggestions: report.suggestions || getDefaultReport().suggestions,
          detailedAnalysis:
            report.detailedAnalysis || getDefaultReport().detailedAnalysis,
          nextSteps: report.nextSteps || getDefaultReport().nextSteps,
          behaviorAnalysis: report.behaviorAnalysis || undefined,
        };
      }
    } catch {
      // 解析失败，返回默认报告
    }

    // 返回默认报告
    return getDefaultReport();
  } catch {
    return getDefaultReport();
  }
}

// 默认回应
function getDefaultResponse(
  config: InterviewConfig,
  isFirstMessage: boolean,
): string {
  if (isFirstMessage) {
    return `你好，欢迎参加今天的面试。我是你的面试官，我们将进行一场${config.level}${config.jobType}的${config.type}。面试会根据你的回答情况进行，我会根据你的表现灵活提问，没有固定的题目数量。请放松，准备好后我们可以开始。`;
  }

  const defaultQuestions: Record<string, string[]> = {
    技术面试: [
      '请介绍一下你最熟悉的技术栈，以及你在其中的实践经验。',
      '描述一次你解决过的复杂技术问题。',
      '请解释一下你熟悉的设计模式。',
      '如何保证代码质量和可维护性？',
    ],
    行为面试: [
      '请分享一个你在团队中发挥重要作用的经历。',
      '描述一次你面对压力的经历。',
      '请举例说明你如何处理与同事的分歧。',
      '分享一个你主动学习新技能的经历。',
    ],
    综合面试: [
      '请简单介绍一下你自己。',
      '你为什么想应聘这个岗位？',
      '描述一个你最有成就感的项目。',
      '你对未来的职业发展有什么规划？',
    ],
  };

  const questions =
    defaultQuestions[config.type] || defaultQuestions['综合面试'];
  return questions[Math.floor(Math.random() * questions.length)];
}

// 获取面试历史详情
export async function fetchInterviewDetail(id: number): Promise<{
  id: number;
  job_type: string;
  job_level: string;
  interview_type: string;
  interviewer_id: string;
  score: number;
  created_at: number;
  messages?: Message[];
  report?: InterviewReport;
} | null> {
  try {
    const response = await authFetch(`${API_BASE_URL}/api/interviews/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('获取面试详情失败');
    }

    const data = await response.json();

    // 解析 messages JSON 字符串
    if (data.messages && typeof data.messages === 'string') {
      try {
        data.messages = JSON.parse(data.messages);
      } catch {
        data.messages = [];
      }
    }

    // 解析 report JSON 字符串
    if (data.report && typeof data.report === 'string') {
      try {
        data.report = JSON.parse(data.report);
      } catch {
        data.report = null;
      }
    }

    return data;
  } catch {
    return null;
  }
}

// 默认报告
function getDefaultReport() {
  return {
    totalScore: 80,
    dimensions: {
      expression: 82,
      technical: 78,
      logic: 80,
      communication: 85,
      professionalism: 83,
      learning: 81,
      overall: 81,
    },
    strengths: [
      '基础概念掌握扎实，回答思路清晰有条理',
      '沟通态度积极主动，表达流畅自然有亲和力',
      '具备一定的项目实践经验，能将理论应用到实际',
      '对行业和技术趋势有基本了解，有持续学习的意识',
    ],
    suggestions: [
      {
        category: '表达能力',
        content:
          '建议在回答问题时采用"总-分-总"的结构，先给出核心观点，再用具体案例支撑，最后总结强调。这样能让面试官更容易抓住你的重点。',
      },
      {
        category: '技术深度',
        content:
          '技术问题的回答可以更加深入，不仅要说明"做了什么"，更要解释"为什么这样做"以及"遇到了什么挑战如何解决的"。',
      },
      {
        category: '沟通节奏',
        content:
          '回答前可以稍作停顿整理思路，保持适中的语速。适当的沉默比匆忙回答更能体现你的沉稳和专业。',
      },
      {
        category: '项目经验',
        content:
          '建议使用 STAR 法则（情境-任务-行动-结果）来描述项目经历，量化你的贡献和成果会更有说服力。',
      },
    ],
    detailedAnalysis: {
      expression:
        '你的表达能力整体良好，语言流畅，能够清晰地传达想法。在描述复杂概念时，可以适当使用类比或举例来增强理解。建议在回答前先在脑海中组织好要点，按照重要程度依次阐述。',
      technical:
        '技术基础扎实，对核心概念有较好的理解。在回答技术问题时，能够给出正确的方向。建议在面对开放性问题时，可以多分享一些实际案例，展示你解决问题的思路和方法。',
      logic:
        '逻辑思维较为清晰，能够按照一定的条理回答问题。在分析复杂问题时，建议采用结构化的方式，比如"首先...其次...最后"，让面试官更容易跟上你的思路。',
      communication:
        '沟通态度积极，能够认真倾听问题并给出回应。在互动过程中表现得体。建议在面试中多观察面试官的反应，适时调整回答的深度和节奏，保持良好的互动氛围。',
      professionalism:
        '整体职业素养良好，展现出对面试的重视和对岗位的尊重。建议继续保持这种专业的态度，同时可以在回答中适当展现你对公司文化的了解和认同。',
      learning:
        '展现出较强的学习意愿和成长潜力，对新技术持开放态度。建议在学习新知识的同时，多思考如何将其应用到实际工作中，形成自己的知识体系。',
    },
    nextSteps: [
      '针对面试中回答不够理想的问题，进行复盘和总结，准备好更完善的答案',
      '练习使用 STAR 法则描述项目经历，准备 3-5 个不同场景的案例',
      '保持学习的习惯，关注行业动态，提升技术深度和广度',
    ],
  };
}
